import { createColumnHelper } from '@tanstack/react-table'
import React from 'react'
import { Container } from '../../../components/commons'
import Table from '../../../components/Table'
import Pagination from '../../../components/Table/Pagination'
import useUsers, { User } from '../../../services/users.service'
import { api } from '../../../services/api'
import TableActions from '../../../components/Table/TableActions'
import ModalDelete from '../../../components/Modals/ModalDelete'
import CreateModal from './CreateModal'
import useToast from '../../../hooks/useToast'
import { NamePhoto, Role } from './styles'
import Popup from 'reactjs-popup'
import { format, isValid } from 'date-fns'
import Avatar from '../../../components/Avatar'
import PermissionGate from '../../../components/PermissionGate'
import PermissionButton from '../../../components/PermissionButton'
import { usePermissions } from '../../../hooks/usePermissions'

export interface UsersProps {}
export type ModalDeleteRef = React.ElementRef<typeof ModalDelete>
type CreateRef = React.ElementRef<typeof CreateModal>

export interface RoleChangeProps {
  _id?: string
  role: string
  refetch: () => void
}

export const RoleChange: React.FC<RoleChangeProps> = ({ _id, role, refetch }) => {
  let rolesNames: any = {
    admin: 'Administrador',
    manager: 'Gerente',
    user: 'Usuário',
    viewer: 'Visualizador',
    digitizer: 'Digitalizador',
  }

  const toast = useToast()

  const handleChangeRole = async (role: string) => {
    try {
      await api.put(`/users/${_id}/`, {
        role: role,
      })

      toast.successToast('Cargo alterado com sucesso!')
      refetch()
    } catch (error) {
      toast.errorToast('Erro ao alterar a role')
    }
  }

  return (
    <>
      <Popup trigger={<Role.Container>{rolesNames[role]}</Role.Container>} position='bottom center'>
        <Role.Menu>
          {Object.keys(rolesNames).map((element) => {
            if (element !== role)
              return (
                <Role.Item onClick={() => handleChangeRole(element)}>
                  {rolesNames[element]}
                </Role.Item>
              )
          })}
        </Role.Menu>
      </Popup>
    </>
  )
}

const Users: React.FunctionComponent<UsersProps> = () => {
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState('')
  const [loading, setloading] = React.useState(false)

  const [sort, setSort] = React.useState<any>()

  const { isLoading, data, refetch } = useUsers(page, 18, search, sort)
  const { canEditDocuments, canDeleteDocuments, canManageUsers } = usePermissions()

  const modalCreate = React.useRef<CreateRef>(null)
  const modalDeleteRef = React.useRef<ModalDeleteRef>(null)

  const onSearch = (value: string) => {
    setSearch(value)
  }

  const handleSubmitActive = async (_id: string, active: boolean) => {
    await api.put(`/users/${_id}`, { active })

    refetch()
  }

  const onSortElement = (name: string, sort: string) => {
    setSort({ [name]: sort })
  }

  const handleCreate = () => {
    modalCreate.current?.openFormModal()
  }

  const handleOpenEdit = (initialData?: User) => {
    modalCreate.current?.openFormModal(initialData)
  }

  const handleSubmitDelete = async (_id?: string) => {
    try {
      setloading(true)
      await api.delete(`/users/${_id}`)
      modalDeleteRef.current?.closeModal()
      refetch()
    } catch (error) {
    } finally {
      setloading(false)
    }
  }

  const columnHelper = createColumnHelper<User>()

  const pageChangeHandler = (page: number) => setPage(page)
  const columns = [
    columnHelper.accessor('name', {
      header: () => 'Nome',
      cell: (info) => {
        return (
          <NamePhoto>
            <Avatar source={info?.row?.original?.photo} />
            {info.getValue()}
          </NamePhoto>
        )
      },
    }),
    columnHelper.accessor('email', {
      header: () => 'E-mail',
      cell: (info) => info.renderValue(),
    }),
    columnHelper.accessor('role', {
      header: () => 'Perfil',
      cell: (info) => {
        const role = info.getValue()
        const roleNames: any = {
          admin: 'Administrador',
          manager: 'Gerente',
          user: 'Usuário',
          viewer: 'Visualizador',
          digitizer: 'Digitalizador',
        }
        return <RoleChange _id={info.row.original._id} role={role} refetch={refetch} />
      },
    }),
    columnHelper.accessor('createdAt', {
      header: () => 'Data de criação',
      cell: (info) => {
        try {
          const dateValue = info?.renderValue();
          if (dateValue) {
            const date = new Date(dateValue);
            if (isValid(date)) {
              return format(date, 'dd/MM/yyyy HH:mm');
            }
          }
          return '-';
        } catch (error) {
          console.warn('Erro ao formatar data de usuário:', error);
          return '-';
        }
      },
    }),
    columnHelper.accessor('_id', {
      header: () => 'Ação',
      cell: (info) => {
        return (
          <TableActions
            isActive={info.row.original.active}
            handleActive={() => handleSubmitActive(info.row.original._id, true)}
            handleDesactive={() => handleSubmitActive(info.row.original._id, false)}
            handleEdit={() => handleOpenEdit(info.row.original)}
            handleDelete={() => modalDeleteRef.current?.openModal(info.row.original._id)}
            canEdit={canManageUsers}
            canDelete={canManageUsers}
            canActivate={canManageUsers}
          />
        )
      },
    }),
  ]

  return (
    <Container>
      <CreateModal ref={modalCreate} reload={refetch} />

      <ModalDelete
        loading={loading}
        title='Deletar usuário?'
        ref={modalDeleteRef}
        onSubmitButton={handleSubmitDelete}
      />

      <Table
        title='Usuários'
        onSearch={onSearch}
        isLoading={isLoading}
        onSort={onSortElement}
        create={handleCreate}
        columns={columns}
        data={data?.data}
      />
      <Pagination
        totalPages={data?.totalPages}
        page={page}
        perPage={18}
        pageChangeHandler={pageChangeHandler}
      />
    </Container>
  )
}

export default Users
